/**
 * LoadingSkeletons - Pre-built Skeleton Variants
 *
 * Beautiful, premium loading skeleton components that match
 * the exact dimensions of their corresponding real components.
 *
 * Features:
 * - Shimmer effect with smooth animation
 * - Matches real component dimensions exactly
 * - Responsive sizing
 * - Smooth transition to actual content
 *
 * @example
 * ```tsx
 * // While loading products
 * {isLoading ? <ProductGridSkeleton count={6} /> : <ProductGrid products={products} />}
 * ```
 */

import * as React from 'react';
import {cn} from '@/shared/utils/utils';
import {Skeleton} from './Skeleton';
import type {
    DesignPreviewSkeletonProps,
    ProductCardSkeletonProps,
    ProductGridSkeletonProps,
    TextBlockSkeletonProps,
} from './types';

// ============================================
// PRODUCT CARD SKELETON
// ============================================

const productCardSizes = {
  sm: { width: 'max-w-truncate-md', imageHeight: 'h-32' },
  md: { width: 'max-w-card-md', imageHeight: 'h-44' },
  lg: { width: 'max-w-card-lg', imageHeight: 'h-56' },
} as const;

/**
 * Skeleton matching ProductCard dimensions
 */
export function ProductCardSkeleton({
  size = 'md',
  className,
}: ProductCardSkeletonProps): React.ReactElement {
  const sizeConfig = productCardSizes[size];

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-apple-xl bg-card shadow-apple-md',
        sizeConfig.width,
        className
      )}
      aria-hidden="true"
    >
      {/* Image placeholder */}
      <Skeleton
        variant="rectangular"
        className={cn('w-full', sizeConfig.imageHeight)}
      />

      {/* Content placeholders */}
      <div className="space-y-3 p-4">
        {/* Title */}
        <Skeleton variant="text" width="85%" height={20} />
        {/* Description */}
        <Skeleton variant="text" width="70%" height={16} />
        {/* Price */}
        <Skeleton variant="text" width="40%" height={24} />
      </div>
    </div>
  );
}

// ============================================
// PRODUCT GRID SKELETON
// ============================================

/**
 * Grid of ProductCardSkeletons matching actual product grid layout
 */
export function ProductGridSkeleton({
  count = 6,
  className,
}: ProductGridSkeletonProps): React.ReactElement {
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
        className
      )}
      aria-label="Loading products"
      aria-busy="true"
    >
      {Array.from({ length: count }).map((_, index) => (
        <ProductCardSkeleton key={index} size="md" />
      ))}
    </div>
  );
}

// ============================================
// DESIGN PREVIEW SKELETON
// ============================================

const previewSizes = {
  sm: 'w-32 h-32',
  md: 'w-48 h-48',
  lg: 'w-64 h-64',
  full: 'w-full aspect-square',
} as const;

/**
 * Skeleton matching DesignPreview dimensions
 */
export function DesignPreviewSkeleton({
  size = 'md',
  className,
}: DesignPreviewSkeletonProps): React.ReactElement {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-apple-lg bg-muted/20',
        previewSizes[size],
        className
      )}
      aria-hidden="true"
    >
      <Skeleton variant="rectangular" className="h-full w-full" />
    </div>
  );
}

// ============================================
// TEXT BLOCK SKELETON
// ============================================

/**
 * Multiple text lines with natural varying widths
 */
// Helper function to generate natural-looking line widths
function generateLineWidths(lines: number): string[] {
  const widths: string[] = [];
  for (let i = 0; i < lines; i++) {
    if (i === lines - 1) {
      // Last line is shorter
      widths.push(`${40 + Math.random() * 30}%`);
    } else {
      // Other lines are longer with slight variation
      widths.push(`${80 + Math.random() * 20}%`);
    }
  }
  return widths;
}

export function TextBlockSkeleton({
  lines = 3,
  className,
}: TextBlockSkeletonProps): React.ReactElement {
  // Generate line widths once on mount
  const lineWidths = React.useMemo(() => generateLineWidths(lines), [lines]);

  return (
    <div className={cn('space-y-2', className)} aria-hidden="true">
      {lineWidths.map((width, index) => (
        <Skeleton
          key={index}
          variant="text"
          width={width}
          height={index === 0 ? 20 : 16}
        />
      ))}
    </div>
  );
}

// ============================================
// ORDER DETAIL SKELETON
// ============================================

/**
 * Skeleton matching order detail page layout
 */
export function OrderDetailSkeleton({
  className,
}: {
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn('space-y-6', className)} aria-hidden="true">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton variant="text" width={200} height={28} />
          <Skeleton variant="text" width={120} height={16} />
        </div>
        <Skeleton variant="rectangular" width={100} height={32} className="rounded-full" />
      </div>

      {/* Order items */}
      <div className="rounded-apple-lg border p-4">
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton variant="rectangular" width={80} height={80} className="rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton variant="text" width="60%" height={18} />
                <Skeleton variant="text" width="40%" height={14} />
                <Skeleton variant="text" width="30%" height={16} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Shipping & payment info */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-apple-lg border p-4">
          <Skeleton variant="text" width={100} height={18} className="mb-3" />
          <TextBlockSkeleton lines={4} />
        </div>
        <div className="rounded-apple-lg border p-4">
          <Skeleton variant="text" width={120} height={18} className="mb-3" />
          <TextBlockSkeleton lines={3} />
        </div>
      </div>

      {/* Total */}
      <div className="flex justify-end">
        <div className="w-64 space-y-2">
          <div className="flex justify-between">
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={60} height={16} />
          </div>
          <div className="flex justify-between">
            <Skeleton variant="text" width={60} height={16} />
            <Skeleton variant="text" width={50} height={16} />
          </div>
          <div className="flex justify-between pt-2 border-t">
            <Skeleton variant="text" width={50} height={20} />
            <Skeleton variant="text" width={80} height={20} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// CHECKOUT FORM SKELETON
// ============================================

/**
 * Skeleton matching checkout form layout
 */
export function CheckoutFormSkeleton({
  className,
}: {
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn('space-y-6', className)} aria-hidden="true">
      {/* Contact info */}
      <div className="space-y-4">
        <Skeleton variant="text" width={120} height={20} />
        <Skeleton variant="rectangular" height={44} className="w-full rounded-lg" />
      </div>

      {/* Shipping address */}
      <div className="space-y-4">
        <Skeleton variant="text" width={150} height={20} />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton variant="rectangular" height={44} className="rounded-lg" />
          <Skeleton variant="rectangular" height={44} className="rounded-lg" />
        </div>
        <Skeleton variant="rectangular" height={44} className="w-full rounded-lg" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton variant="rectangular" height={44} className="rounded-lg" />
          <Skeleton variant="rectangular" height={44} className="rounded-lg" />
        </div>
      </div>

      {/* Button */}
      <Skeleton variant="rectangular" height={48} className="w-full rounded-lg" />
    </div>
  );
}

// ============================================
// CART ITEM SKELETON
// ============================================

/**
 * Skeleton matching cart item row
 */
export function CartItemSkeleton({
  className,
}: {
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn('flex gap-4 py-4', className)} aria-hidden="true">
      <Skeleton variant="rectangular" width={80} height={80} className="shrink-0 rounded-lg" />
      <div className="flex flex-1 flex-col justify-between">
        <div className="space-y-1">
          <Skeleton variant="text" width="70%" height={18} />
          <Skeleton variant="text" width="40%" height={14} />
        </div>
        <div className="flex items-center justify-between">
          <Skeleton variant="rectangular" width={100} height={32} className="rounded-md" />
          <Skeleton variant="text" width={60} height={18} />
        </div>
      </div>
    </div>
  );
}

/**
 * Multiple cart item skeletons
 */
export function CartSkeleton({
  count = 3,
  className,
}: {
  count?: number;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn('divide-y', className)} aria-label="Loading cart" aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <CartItemSkeleton key={index} />
      ))}
    </div>
  );
}

// ============================================
// CART SUMMARY SKELETON
// ============================================

/**
 * Skeleton matching cart summary layout with totals
 */
export function CartSummarySkeleton({
  className,
}: {
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn('space-y-3 py-4', className)} aria-hidden="true">
      {/* Subtotal row */}
      <div className="flex justify-between">
        <Skeleton variant="text" width={80} height={16} />
        <Skeleton variant="text" width={70} height={16} />
      </div>
      {/* VAT row */}
      <div className="flex justify-between">
        <Skeleton variant="text" width={100} height={16} />
        <Skeleton variant="text" width={60} height={16} />
      </div>
      {/* Shipping row */}
      <div className="flex justify-between">
        <Skeleton variant="text" width={60} height={16} />
        <Skeleton variant="text" width={50} height={16} />
      </div>
      {/* Divider */}
      <div className="h-px bg-border" />
      {/* Total row */}
      <div className="flex justify-between items-center">
        <Skeleton variant="text" width={50} height={22} />
        <Skeleton variant="text" width={90} height={26} />
      </div>
    </div>
  );
}

// Display names for debugging
ProductCardSkeleton.displayName = 'ProductCardSkeleton';
ProductGridSkeleton.displayName = 'ProductGridSkeleton';
DesignPreviewSkeleton.displayName = 'DesignPreviewSkeleton';
TextBlockSkeleton.displayName = 'TextBlockSkeleton';
OrderDetailSkeleton.displayName = 'OrderDetailSkeleton';
CheckoutFormSkeleton.displayName = 'CheckoutFormSkeleton';
CartItemSkeleton.displayName = 'CartItemSkeleton';
CartSkeleton.displayName = 'CartSkeleton';
CartSummarySkeleton.displayName = 'CartSummarySkeleton';

// ============================================
// ORDER LIST SKELETON
// ============================================

/**
 * Skeleton matching order list card layout (for dashboard/order history)
 */
export function OrderListSkeleton({
  count = 3,
  className,
}: {
  count?: number;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn('space-y-3 sm:space-y-4', className)} aria-label="Loading orders" aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="rounded-apple-lg border p-4 animate-pulse"
          aria-hidden="true"
        >
          <div className="flex items-center gap-4">
            {/* Thumbnail */}
            <Skeleton variant="rectangular" width={64} height={64} className="rounded-lg flex-shrink-0" />

            {/* Content */}
            <div className="flex-1 space-y-2 min-w-0">
              <Skeleton variant="text" width="60%" height={18} />
              <Skeleton variant="text" width="40%" height={14} />
              <div className="flex items-center gap-2 mt-2">
                <Skeleton variant="rectangular" width={80} height={24} className="rounded-full" />
                <Skeleton variant="text" width={100} height={16} />
              </div>
            </div>

            {/* Price */}
            <div className="flex-shrink-0 text-right">
              <Skeleton variant="text" width={60} height={20} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

OrderListSkeleton.displayName = 'OrderListSkeleton';
